import React, {useEffect, useState} from "react";
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

import type {i18n} from "i18next";

// Importing muiLocale also registers the Day.js locales it names (side-effect imports there), so
// dayjs.locale(dayjsLocale) below always finds a loaded locale.
import {resolveDayjsLocale, resolveMuiPickersLocaleText} from "@/app/locale/muiLocale";

export type LocaleProviderProps = {
    i18n: i18n;
    children: React.ReactNode;
};

export const LocaleProvider = ({ i18n, children }: LocaleProviderProps) => {
    // No fallback needed here: both resolvers below already map an unknown/empty code to German.
    const [locale, setLocale] = useState(i18n.language);

    useEffect(() => {
        const handleLanguageChange = (lng: string) => setLocale(lng);

        i18n.on("languageChanged", handleLanguageChange);
        return () => {
            i18n.off("languageChanged", handleLanguageChange);
        };
    }, [i18n]);

    const dayjsLocale = resolveDayjsLocale(locale);

    useEffect(() => {
        dayjs.locale(dayjsLocale); // Set Day.js locale globally
    }, [dayjsLocale]);

    return (
        <LocalizationProvider
            dateAdapter={AdapterDayjs}
            adapterLocale={dayjsLocale}
            localeText={resolveMuiPickersLocaleText(locale)}
        >
            {children}
        </LocalizationProvider>
    );
};
